import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { MapPin, Phone, Mail, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const ref = useRef(null);
  const formRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  const [formData, setFormData] = useState({
    from_name: '',
    from_company: '',
    from_phone: '',
    project_type: '',
    from_email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const projectTypes = [
    'Vivienda Multifamiliar',
    'Inmueble Comercial',
    'Edificación Institucional',
    'Supervisión de Proyecto',
    'Urbanización',
    'Otro'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.sendForm(
        'service_k2nv72g',
        'template_h1onvdu',
        formRef.current,
        'K9AbGbQ0oxEY53Gbr'
      );

      setIsSubmitted(true);
      toast.success('¡Mensaje enviado exitosamente!', {
        description: 'Nos pondremos en contacto contigo pronto.'
      });
      
      setFormData({
        from_name: '',
        from_company: '',
        from_phone: '',
        project_type: '',
        from_email: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS error:', error);
      toast.error('Error al enviar el mensaje', {
        description: 'Por favor intenta de nuevo más tarde.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contacto" 
      className="py-24 md:py-32 bg-[#F8F9FA]"
      data-testid="contact-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-[2px] bg-[#2E75B6]"></div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#2E75B6]">
              Contacto
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#203864]">
            Hablemos de tu Proyecto
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-8"
          >
            <p className="text-[#7F7F7F] leading-relaxed">
              Cuéntanos sobre tu proyecto y un especialista se pondrá en contacto contigo 
              para brindarte una asesoría personalizada sin compromiso.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#203864] flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-[#203864] mb-1">Dirección</h4>
                  <p className="text-[#7F7F7F] text-sm">
                    Calle Jorge Santayana No. 4965, col. Jardines Universidad, C.P. 45110, Zapopan, Jalisco.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#203864] flex items-center justify-center flex-shrink-0">
                  <Phone size={18} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-[#203864] mb-1">Teléfono</h4>
                  <a href="tel:+523318231064" className="text-[#7F7F7F] text-sm hover:text-[#2E75B6] transition-colors">
                    33 1823 1064
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#203864] flex items-center justify-center flex-shrink-0">
                  <Mail size={18} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-[#203864] mb-1">Email</h4>
                  <a href="mailto:contacto@eficapro.com" className="text-[#7F7F7F] text-sm hover:text-[#2E75B6] transition-colors">
                    contacto@eficapro.com
                  </a>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="h-56 overflow-hidden border border-gray-200">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3732.521877630974!2d-103.42030022529455!3d20.689022099361743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8428aeede132dac1%3A0xf70b6f5980449e8a!2sC.%20Jorge%20Santayana%204965%2C%20Jardines%20Universidad%2C%2045110%20Zapopan%2C%20Jal.!5e0!3m2!1ses!2smx!4v1775604765450!5m2!1ses!2smx" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación EFICAPRO"
                data-testid="google-map"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <div className="bg-white p-8 lg:p-10 shadow-sm">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#203864] mb-4">
                    ¡Gracias por contactarnos!
                  </h3>
                  <p className="text-[#7F7F7F] mb-6">
                    Hemos recibido tu mensaje. Un especialista se pondrá en contacto contigo pronto.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-[#2E75B6] font-semibold hover:underline"
                    data-testid="send-another-message"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form 
                  ref={formRef}
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-[#203864] mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        name="from_name"
                        value={formData.from_name}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 bg-[#F8F9FA] px-4 py-3 text-[#3B3838] placeholder-gray-400 focus:border-[#2E75B6] focus:outline-none focus:bg-white transition-colors"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#203864] mb-2">
                        Empresa
                      </label>
                      <input
                        type="text"
                        name="from_company"
                        value={formData.from_company}
                        onChange={handleChange}
                        className="w-full border border-gray-200 bg-[#F8F9FA] px-4 py-3 text-[#3B3838] placeholder-gray-400 focus:border-[#2E75B6] focus:outline-none focus:bg-white transition-colors"
                        data-testid="input-company"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-[#203864] mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        name="from_phone"
                        value={formData.from_phone}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 bg-[#F8F9FA] px-4 py-3 text-[#3B3838] placeholder-gray-400 focus:border-[#2E75B6] focus:outline-none focus:bg-white transition-colors"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#203864] mb-2">
                        Tipo de proyecto *
                      </label>
                      <select
                        name="project_type"
                        value={formData.project_type}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 bg-[#F8F9FA] px-4 py-3 text-[#3B3838] focus:border-[#2E75B6] focus:outline-none focus:bg-white transition-colors appearance-none cursor-pointer"
                        data-testid="select-project-type"
                      >
                        <option value="" disabled>Seleccionar...</option>
                        {projectTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#203864] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="from_email"
                      value={formData.from_email}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-200 bg-[#F8F9FA] px-4 py-3 text-[#3B3838] placeholder-gray-400 focus:border-[#2E75B6] focus:outline-none focus:bg-white transition-colors"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#203864] mb-2">
                      Cuéntanos sobre tu proyecto *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full border border-gray-200 bg-[#F8F9FA] px-4 py-3 text-[#3B3838] placeholder-gray-400 focus:border-[#2E75B6] focus:outline-none focus:bg-white transition-colors resize-none"
                      data-testid="textarea-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#203864] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#2E75B6] transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Enviar Mensaje
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
